import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, parseISO } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date, fmt = "MMM d, yyyy") {
  const d = typeof date === "string" ? parseISO(date) : date;
  return format(d, fmt);
}

export function formatCurrency(amount: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    amount
  );
}

export function getInitials(firstName?: string | null, lastName?: string | null) {
  return `${(firstName || "")[0] || ""}${(lastName || "")[0] || ""}`.toUpperCase();
}

/**
 * Render a person's display name, defensively de-duping the last
 * name so we never produce "Laura Arce Arce" — regardless of how
 * mangled the underlying data is.
 *
 * Four data shapes shipped to production that all produced the
 * tripled-token output. The previous version of this helper
 * handled three; the fourth was the one still leaking through:
 *
 *   1. ``first_name = "Laura Arce"``, ``last_name = "Arce"``
 *   2. ``last_name = "Arce Arce"`` (doubled-word column)
 *   3. ``preferred_name = "Laura Arce"``, ``last_name = "Arce"``
 *   4. ``preferred_name = "Laura Arce Arce"`` — the DB row itself
 *      already has the doubling baked into ONE column. Naive
 *      "if display endsWith last, return display" returns the
 *      triple-token string verbatim.
 *
 * Algorithm:
 *   a. Collapse the doubled-last-name shape ("Arce Arce" → "Arce").
 *   b. Strip trailing duplicate tokens from the display itself —
 *      "Laura Arce Arce" becomes "Laura Arce" before we do any
 *      last-name logic. This is the fix for shape #4 and is also
 *      a safety net for any future doubling vector.
 *   c. If the cleaned display already ends with the (collapsed)
 *      last name, return it as-is. Otherwise append ` ${last}`.
 *
 * The whole pipeline is case-insensitive (so "ARCE arce" is
 * detected as a dup) and respects whole-token boundaries (so
 * "Carce" isn't treated as ending in "ce").
 */
/**
 * Normalize ANY Unicode whitespace to a regular space + trim.
 *
 * THE bug that kept "Laura Arce Arce" alive across three frontend
 * fixes: production data has NBSP (U+00A0) between "Laura" and
 * "Arce" inside ``first_name``. ``split(/\s+/)`` matches NBSP just
 * fine, but the ``endsWith(" " + lastLower)`` guard further down
 * checks for U+0020 specifically — NBSP doesn't match → fallthrough
 * appends a second " Arce" → renders as the doubled string.
 *
 * Normalizing at function entry means every downstream comparison
 * sees a single canonical character ("Laura Arce" → "Laura Arce"),
 * so the existing endsWith / token-pop logic actually fires.
 *
 * ``\s`` in JS regex matches NBSP, narrow-NBSP, em-space, en-space,
 * ideographic space, ZWNBSP and the rest of the Unicode-whitespace
 * family — so this one regex catches all the LLM/copy-paste
 * artifacts that produce these rows in the first place.
 */
function _normalizeNameWhitespace(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}


export function fullName(
  firstName?: string | null,
  lastName?: string | null,
  preferredName?: string | null
) {
  let display = _normalizeNameWhitespace(preferredName || firstName || "");
  let last = _normalizeNameWhitespace(lastName || "");

  // (a) Collapse doubled-word last names in-flight. Loops so
  // "Arce Arce Arce" → "Arce" in one pass (the previous 2-token-
  // only check would have left "Arce Arce Arce" untouched).
  if (last) {
    while (true) {
      const lt = last.split(" ");
      if (lt.length < 2) break;
      const tail = lt[lt.length - 1].toLowerCase();
      const prev = lt[lt.length - 2].toLowerCase();
      if (tail !== prev) break;
      lt.pop();
      last = lt.join(" ");
    }
  }

  // (b) Strip ALL adjacent duplicate tokens from the display itself —
  // anywhere in the string, not just at the trailing position. This
  // catches "Laura Laura Arce" (dup at start) in addition to
  // "Laura Arce Arce" (dup at end). Case-insensitive comparison so
  // "ARCE arce" still matches. Loops until no more dedup happens.
  // NB: we split on a literal space because the normalizer above
  // guarantees no NBSPs are left in ``display``.
  while (true) {
    const tokens = display.split(" ").filter(Boolean);
    if (tokens.length < 2) break;
    const deduped: string[] = [];
    for (const tok of tokens) {
      if (
        deduped.length > 0 &&
        deduped[deduped.length - 1].toLowerCase() === tok.toLowerCase()
      ) {
        continue;
      }
      deduped.push(tok);
    }
    if (deduped.length === tokens.length) break;
    display = deduped.join(" ");
  }

  if (!last) return display;
  if (!display) return last;

  // (c) Skip appending if display already ends with last_name
  // (case-insensitive whole-word). The leading space in the
  // ``" " + lastLower`` guard prevents "Carce".endsWith("rce")
  // from accidentally matching. Both sides are post-normalize so
  // the space is guaranteed to be U+0020.
  const lower = display.toLowerCase();
  const lastLower = last.toLowerCase();
  if (lower === lastLower || lower.endsWith(" " + lastLower)) {
    return display;
  }
  return `${display} ${last}`;
}

export const pageTransition = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.2, ease: "easeOut" as const },
};
